import React, {useState, useEffect} from "react";
import {Download} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import {Close as CloseIcon} from "@mui/icons-material";
import {useNotifications} from "../NotificationSystem";
import {
  getSessionCharacterSheets,
  SessionCharacterSheet,
} from "../../services/api/characterSheetService";
// Viewer already receives short-lived URLs from server via getSessionCharacterSheets

interface CharacterSheetViewerProps {
  sessionId: string;
  sessionName: string;
  isOpen: boolean;
  onClose: () => void;
}

const CharacterSheetViewer: React.FC<CharacterSheetViewerProps> = ({
  sessionId,
  sessionName,
  isOpen,
  onClose,
}) => {
  const {addNotification} = useNotifications();
  const [characterSheets, setCharacterSheets] = useState<
    SessionCharacterSheet[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCharacterSheets = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getSessionCharacterSheets(sessionId);
        // Handle the response structure from Firebase function
        const sheets = result.characterSheets || [];
        setCharacterSheets(sheets);
      } catch (error: any) {
        // Error loading character sheets - handled by error state and notification
        setError(error.message || "Failed to load character sheets");
        addNotification({
          type: "error",
          title: "Load Failed",
          message: error.message || "Failed to load character sheets",
        });
      } finally {
        setLoading(false);
      }
    };

    loadCharacterSheets();
  }, [sessionId, addNotification]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Character Sheets - {sessionName}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box className="env-loading-state">
            <div className="loading-spinner"></div>
            <Typography>Loading character sheets...</Typography>
          </Box>
        ) : error ? (
          <Box className="env-error-state">
            <Typography color="error">Error: {error}</Typography>
          </Box>
        ) : (
          <Box className="env-character-sheets-list">
            {characterSheets.length === 0 ? (
              <Box className="env-empty-state">
                <Typography>
                  No character sheets available for this session.
                </Typography>
              </Box>
            ) : (
              characterSheets.map((sheet) => (
                <Box
                  key={sheet.characterId}
                  className="env-character-sheet-item"
                >
                  <Box className="env-character-sheet-info">
                    <Box className="env-character-sheet-player">
                      <Typography component="strong" fontWeight="bold">
                        {sheet.playerName}
                      </Typography>
                      <Typography
                        component="span"
                        className="env-character-sheet-character"
                        sx={{ml: 1}}
                      >
                        {sheet.characterName} ({sheet.characterClass}{" "}
                        {sheet.characterLevel})
                      </Typography>
                    </Box>
                    {sheet.hasSheet ? (
                      <Box className="env-character-sheet-actions">
                        <a
                          href={sheet.sheetUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn env-btn-link"
                        >
                          <Download size={16} style={{marginRight: "8px"}} />
                          Download
                        </a>
                      </Box>
                    ) : (
                      <Box className="env-character-sheet-no-sheet">
                        <Typography component="span">
                          No sheet uploaded
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CharacterSheetViewer;
